import React, { useState, useRef, PropsWithChildren, DragEvent } from 'react';

type DropzoneProps = {
  className?: string;
  disabled?: boolean;
} & (
  | {
      onDrop(e: FileList): void;
      onlyFiles?: never | true;
    }
  | {
      onDrop(e: DragEvent): void;
      onlyFiles: false;
    }
);

const Dropzone = ({
  children,
  onDrop,
  className = '',
  onlyFiles = true,
  disabled = false
}: PropsWithChildren<DropzoneProps>) => {
  const [dragEnabled, setDragEnabled] = useState(false);
  const dragCounter = useRef(0);

  const preventEventDefaults = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: DragEvent) => {
    preventEventDefaults(e);

    dragCounter.current += 1;
    if (
      (e.dataTransfer?.items && e.dataTransfer.items.length > 0) ||
      !onlyFiles
    ) {
      setDragEnabled(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    preventEventDefaults(e);

    dragCounter.current -= 1;

    if (dragCounter.current > 0) return;

    setDragEnabled(false);
  };

  const handleDropCb = (e: DragEvent) => {
    if (onlyFiles) {
      const handlerCb = onDrop as (e: FileList) => void;

      handlerCb(e.dataTransfer.files);

      return;
    }

    const handlerCb = onDrop as (e: DragEvent) => void;

    handlerCb(e);
  };

  const handleDrop = (e: DragEvent) => {
    preventEventDefaults(e);

    if (disabled) {
      return null;
    }

    if (
      (e.dataTransfer.files && e.dataTransfer.files.length > 0) ||
      !onlyFiles
    ) {
      setDragEnabled(false);
      dragCounter.current = 0;

      handleDropCb(e);
    }
  };

  const showDragMsg = () => {
    if (dragEnabled && !disabled) {
      return (
        <div className="drag-enabled">
          <p className="drag-enabled__text">Drop it here</p>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={`dropzone ${className}`}
      onDragEnter={handleDragEnter}
      onDragOver={preventEventDefaults}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {showDragMsg()}
      {children}
    </div>
  );
};

export default Dropzone;
